import { Box, Grid, useTheme } from '@mui/material';
import { H5, Small } from 'components/Typography';
import { MessageCircle, Star, ThumbsUp } from 'lucide-react';
import { useState } from 'react';

const CommunityPostTitle = ({ item, isMobile, onClick, ...props }) => {
  const theme = useTheme();
  const [isStarred, setIsStarred] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [starCount, setStarCount] = useState(item.starCount || 0);
  const [likeCount, setLikeCount] = useState(item.likeCount || 0);

  const handleClick = () => {
    if (onClick) {
      onClick(item);
    }
  };

  const handleStarClick = e => {
    e.stopPropagation();
    setIsStarred(!isStarred);
    setStarCount(prev => (isStarred ? prev - 1 : prev + 1));
  };

  const handleLikeClick = e => {
    e.stopPropagation();
    setIsLiked(!isLiked);
    setLikeCount(prev => (isLiked ? prev - 1 : prev + 1));
  };

  return (
    <Grid onClick={handleClick} item xs={12} key={item.id} {...props}>
      <Box
        sx={{
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': isMobile
            ? {}
            : {
                transform: 'translateY(-2px)',
              },
        }}
      >
        {isMobile ? (
          // Mobile Layout: Title Top, Stats Bottom Right
          <>
            <Box mb={0.5}>
              <H5
                sx={{
                  fontWeight: 'bold',
                  lineHeight: 1.3,
                  fontSize: '16px',
                  color: theme.palette.text.primary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.title}
              </H5>
            </Box>

            <Box display="flex" justifyContent="flex-end">
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                  flexShrink: 0,
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  onClick={handleStarClick}
                >
                  <Star
                    size={14}
                    color={
                      isStarred
                        ? theme.palette.primary.mainBlue
                        : theme.palette.primary.greytxt
                    }
                    fill={isStarred ? theme.palette.primary.mainBlue : 'none'}
                  />
                  <Small
                    sx={{
                      color: isStarred
                        ? theme.palette.primary.mainBlue
                        : theme.palette.primary.greytxt,
                      fontSize: '12px',
                      fontWeight: isStarred ? '600' : 'normal',
                    }}
                  >
                    {starCount}
                  </Small>
                </Box>

                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  onClick={handleLikeClick}
                >
                  <ThumbsUp
                    size={14}
                    color={
                      isLiked
                        ? theme.palette.primary.mainBlue
                        : theme.palette.primary.greytxt
                    }
                    fill={isLiked ? theme.palette.primary.mainBlue : 'none'}
                  />
                  <Small
                    sx={{
                      color: isLiked
                        ? theme.palette.primary.mainBlue
                        : theme.palette.primary.greytxt,
                      fontSize: '12px',
                      fontWeight: isLiked ? '600' : 'normal',
                    }}
                  >
                    {likeCount}
                  </Small>
                </Box>

                <Box display="flex" alignItems="center" gap={0.5}>
                  <MessageCircle
                    size={14}
                    color={theme.palette.primary.greytxt}
                    fill="none"
                  />
                  <Small
                    sx={{
                      color: theme.palette.primary.greytxt,
                      fontSize: '12px',
                    }}
                  >
                    {item.commentCount}
                  </Small>
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          // Desktop Layout (Original)
          <Box display="flex" alignItems="center" gap={1.5}>
            <H5
              sx={{
                fontWeight: 'bold',
                lineHeight: 1.3,
                fontSize: '15px',
                color: theme.palette.text.primary,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {item.title}
            </H5>

            {/* Engagement Metrics */}
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              sx={{
                flexShrink: 0,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                onClick={handleStarClick}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <Star
                  size={18}
                  color={
                    isStarred
                      ? theme.palette.primary.mainBlue
                      : theme.palette.primary.greytxt
                  }
                  fill={isStarred ? theme.palette.primary.mainBlue : 'none'}
                />
                <Small
                  sx={{
                    color: isStarred
                      ? theme.palette.primary.mainBlue
                      : theme.palette.primary.greytxt,
                    fontSize: '14px',
                    fontWeight: isStarred ? '600' : 'normal',
                  }}
                >
                  {starCount}
                </Small>
              </Box>

              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                onClick={handleLikeClick}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <ThumbsUp
                  size={18}
                  color={
                    isLiked
                      ? theme.palette.primary.mainBlue
                      : theme.palette.primary.greytxt
                  }
                  fill={isLiked ? theme.palette.primary.mainBlue : 'none'}
                />
                <Small
                  sx={{
                    color: isLiked
                      ? theme.palette.primary.mainBlue
                      : theme.palette.primary.greytxt,
                    fontSize: '14px',
                    fontWeight: isLiked ? '600' : 'normal',
                  }}
                >
                  {likeCount}
                </Small>
              </Box>

              <Box display="flex" alignItems="center" gap={0.5}>
                <MessageCircle
                  size={18}
                  color={theme.palette.primary.greytxt}
                  fill="none"
                />
                <Small
                  sx={{
                    color: theme.palette.primary.greytxt,
                    fontSize: '14px',
                  }}
                >
                  {item.commentCount}
                </Small>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Grid>
  );
};

export default CommunityPostTitle;
